import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { ReactNode } from 'react';
import { cn } from '../../lib/cn';

/**
 * Radix Dropdown wrapper themed to match the app. Used by the user menu,
 * row action menus, and anywhere else we need a hover/click popup of
 * options.
 */

export const DropdownMenu = DropdownMenuPrimitive.Root;
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

export function DropdownMenuContent({
  children,
  align = 'end',
  className,
}: {
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
  className?: string;
}) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        align={align}
        sideOffset={6}
        className={cn(
          'z-50 min-w-[12rem] rounded-lg border border-ink-200 dark:border-ink-800',
          'bg-white dark:bg-ink-900 shadow-lg p-1',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          className,
        )}
      >
        {children}
      </DropdownMenuPrimitive.Content>
    </DropdownMenuPrimitive.Portal>
  );
}

export function DropdownMenuItem({
  children,
  onSelect,
  destructive,
  className,
}: {
  children: ReactNode;
  onSelect?: () => void;
  destructive?: boolean;
  className?: string;
}) {
  return (
    <DropdownMenuPrimitive.Item
      onSelect={onSelect}
      className={cn(
        'flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer outline-none',
        destructive
          ? 'text-warn-700 dark:text-warn-300 data-[highlighted]:bg-warn-50 dark:data-[highlighted]:bg-warn-900/30'
          : 'text-ink-700 dark:text-ink-200 data-[highlighted]:bg-ink-100 dark:data-[highlighted]:bg-ink-800',
        className,
      )}
    >
      {children}
    </DropdownMenuPrimitive.Item>
  );
}

export const DropdownMenuSeparator = () => (
  <DropdownMenuPrimitive.Separator className="my-1 h-px bg-ink-200 dark:bg-ink-800" />
);

export function DropdownMenuLabel({ children }: { children: ReactNode }) {
  return (
    <DropdownMenuPrimitive.Label className="px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-ink-500">
      {children}
    </DropdownMenuPrimitive.Label>
  );
}
